<script setup lang="ts">
import { RoleEnum } from "~/types/User";
import type { EmblaPluginType } from "embla-carousel";
import Autoplay from "embla-carousel-autoplay";
import { Carousel } from '@/components/ui/carousel'
import type {SpecificationType} from "~/types/Specification";
import { Card } from '~/components/ui/card'
import { CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '~/components/ui/carousel'

const { locale, locales } = useI18n()
const userStore = useUserStore()
const route = useRoute()

const props = defineProps({
    items: {
        type: Array as PropType<SpecificationType[]>,
        required: true,
    },
    role: {
        type: String,
    },
    has_arrows: {
        type: Boolean,
        default: false,
    }, 
    has_autoplay: {
        type: Boolean,
        default: false
    }
})

const current_path = computed(() => {
    if(route.name === 'home'){
        return userStore.currentRole === RoleEnum.MASTER ? 'task_category' : 'master_category'
    }
    return route.path.includes('task') ? 'task_category' : 'master_category'
})

const carouselContainerRef = ref<InstanceType<typeof Carousel> | null>(null)

const autoplay = Autoplay({
    delay: 2000,
    stopOnMouseEnter: true,
    stopOnInteraction: false,
})

const plugins = ref<EmblaPluginType[]>([])
const key = ref(1)
const direction = computed(() => {
    return locale.value === 'ar' ? 'rtl' : 'ltr'
})

if(props.has_autoplay){
    plugins.value.push(autoplay)
}

watch(locale, (newLocale) => {
    nextTick(() => {
        //carouselContainerRef.value?.carouselApi?.destroy()
        carouselContainerRef.value?.carouselApi?.reInit(
            {
                direction: locale.value === 'ar' ? 'rtl' : 'ltr'
            },
        )
        autoplay.reset()
        //key.value++
    })
});

</script>

<template>
    <Carousel 
        :key="key"
        ref="carouselContainerRef"
        :opts="{
            align: 'center',
            loop: false,
            direction: direction
        }"
        :plugins="plugins"
        :class="{
            'with_arrows': has_arrows
        }">
        <CarouselContent class="-ml-7">
            <CarouselItem v-for="item in items" :key="item.documentId" class="basis-3/9 md:basis-2/9 lg:basis-1/9 pl-7">
                <NuxtLink :to="{name: current_path, params: {slug: item.slug, city: userStore.currentCity?.slug}}">
                    <Card
                        class="border-0 bg-opacity-0 shadow-none ys-category-box-element cursor-pointer">
                            <div class="mx-auto border border-zinc-300 zoom-image ys-category-small-img-box flex justify-center items-center my-2 bg-[#F6F8FA]">
                                <NuxtImg class="ys-category-small-img min-h-full min-w-full" :src="item.thumbnail?.url" :alt="item.name" placeholder="/img_placeholder.svg" />
                            </div>
                            <p class="font-medium text-center line-clamp-3">{{ item.name }}</p>
                    </Card>
                </NuxtLink>
            </CarouselItem>
        </CarouselContent>

        <template v-if="has_arrows">
            <CarouselPrevious class="categories_carousel__nav_btn sm:flex hidden w-[48px] h-[48px] border-0 bg-stroke hover:bg-gray-300 prev"/>
            <CarouselNext class="categories_carousel__nav_btn sm:flex hidden w-[48px] h-[48px] border-0 bg-stroke hover:bg-gray-300 next"/>
        </template>
        
    </Carousel>
</template>

<style scoped lang="scss">
.with_arrows{
    max-width: calc(100% - 96px);
    padding-left: 24px;
    padding-right: 24px;
    margin: 0 auto;
    @media(max-width: 640px){
        max-width: 100%;
        padding: 0;
    }
}

.zoom-image {
    overflow: hidden;
    img {
        transition: transform 0.3s ease;
        transform: scale(1.1);

        &:hover{
            transform: scale(1);
        }
    }
}

.no-select {
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
}

.ys-category-box-element {
    font-size: 14px;
}

.ys-category-small-img-box {
    max-width: 76px;
    width: 100%;
    height: 76px;
    border-radius: 20px;
}

.ys-category-small-img {
    object-fit: cover;
}
</style>